/* RESPONSIBLE TEAM: team-phone */
import {
  type CallRecording,
  type CallRecordingWireFormat,
  type Message as RecordingMessage,
  deserializeCallRecording,
  createLegacyRecording,
  serializeCallRecording,
} from './call-recording';

export type Message = RecordingMessage;

/**
 * Helper function to create recordings from legacy API fields.
 * Used for backwards compatibility with older API responses.
 */
function createLegacyRecordingsFromFields(json: CallingDetailWireFormat): CallRecording[] {
  let recordings: CallRecording[] = [];

  if (json.recording_url) {
    recordings.push(
      createLegacyRecording(
        `main-${json.id}`,
        'call',
        json.recording_url,
        json.transcript_url,
        json.human_transcript,
        json.json_transcript,
        json.transcript_status,
        json.recording_name,
        json.initiated_at,
        json.ended_at,
        json.duration,
      ),
    );
  }

  if (json.fin_leg_recording_url) {
    recordings.push(
      createLegacyRecording(
        `fin-${json.id}`,
        'fin_voice',
        json.fin_leg_recording_url,
        json.fin_leg_transcript_url,
        null, // Legacy fin leg doesn't have human transcript
        json.fin_leg_json_transcript,
        null, // Legacy fin leg doesn't have transcript status
        null, // Legacy fin leg doesn't have recording name
        json.initiated_at,
        json.ended_at,
        json.duration,
      ),
    );
  }

  return recordings;
}

export interface CallingDetailWireFormat {
  id: number;
  external_id: string | null;
  recording_enabled: boolean;
  type: string;
  state: string;
  direction: string;
  ended_reason: string | null;
  duration: number;
  answered_at: string;
  created_at: string;
  initiated_at: string;
  ended_at: string;
  routing_time: string | null;
  user_id: string;
  admin_id: number;
  phone: string;
  human_transcript: string | null;
  json_transcript: Message[] | null;
  recording_url: string | null;
  conversation_id: number;
  transcript_url: string | null;
  transcript_status: string | null;
  recording_name: string | null;
  forwarded_to: string | null;
  detailed_state: string;
  is_recorded: boolean;
  workspace_phone_number: string;
  csat_rating: string | null;
  fin_leg_recording_url: string | null;
  fin_leg_json_transcript: Message[] | null;
  fin_leg_transcript_url: string | null;
  terminating_party_type: string | null;
  // New array-based recordings
  recordings?: CallRecordingWireFormat[];
}

export class CallingDetail {
  readonly recordings: CallRecording[];
  readonly id: number;
  readonly externalId: string | null;
  readonly recordingEnabled: boolean;
  readonly type: string;
  readonly state: string;
  readonly direction: string;
  readonly endedReason: string | null;
  readonly duration: number;
  readonly answeredAt: string;
  readonly createdAt: string;
  readonly initiatedAt: string;
  readonly endedAt: string;
  readonly routingTime: string | null;
  readonly userId: string;
  readonly adminId: number;
  readonly phone: string;
  readonly humanTranscript: string | null;
  readonly jsonTranscript: Message[] | null;
  readonly recordingUrl: string | null;
  readonly conversationId: number;
  readonly transcriptUrl: string | null;
  readonly transcriptStatus: string | null;
  readonly recordingName: string | null;
  readonly forwardedTo: string | null;
  readonly detailedState: string;
  readonly isRecorded: boolean;
  readonly workspacePhoneNumber: string;
  readonly csatRating: string | null;
  readonly finLegRecordingUrl: string | null;
  readonly finLegJsonTranscript: Message[] | null;
  readonly finLegTranscriptUrl: string | null;
  readonly terminatingPartyType: string | null;

  constructor(
    id: number,
    externalId: string | null,
    recordingEnabled: boolean,
    type: string,
    state: string,
    direction: string,
    endedReason: string | null,
    duration: number,
    answeredAt: string,
    createdAt: string,
    initiatedAt: string,
    endedAt: string,
    routingTime: string | null,
    userId: string,
    adminId: number,
    phone: string,
    humanTranscript: string | null,
    jsonTranscript: Message[] | null,
    recordingUrl: string | null,
    conversationId: number,
    transcriptUrl: string | null,
    transcriptStatus: string | null,
    recordingName: string | null,
    forwardedTo: string | null,
    detailedState: string,
    isRecorded: boolean,
    workspacePhoneNumber: string,
    csatRating: string | null,
    finLegRecordingUrl: string | null,
    finLegJsonTranscript: Message[] | null,
    finLegTranscriptUrl: string | null,
    terminatingPartyType: string | null,
    recordings: CallRecording[] = [],
  ) {
    this.recordings = recordings;
    this.id = id;
    this.externalId = externalId;
    this.recordingEnabled = recordingEnabled;
    this.type = type;
    this.state = state;
    this.direction = direction;
    this.endedReason = endedReason;
    this.duration = duration;
    this.answeredAt = answeredAt;
    this.createdAt = createdAt;
    this.initiatedAt = initiatedAt;
    this.endedAt = endedAt;
    this.routingTime = routingTime;
    this.userId = userId;
    this.adminId = adminId;
    this.phone = phone;
    this.humanTranscript = humanTranscript;
    this.jsonTranscript = jsonTranscript;
    this.recordingUrl = recordingUrl;
    this.conversationId = conversationId;
    this.transcriptUrl = transcriptUrl;
    this.transcriptStatus = transcriptStatus;
    this.recordingName = recordingName;
    this.forwardedTo = forwardedTo;
    this.detailedState = detailedState;
    this.isRecorded = isRecorded;
    this.workspacePhoneNumber = workspacePhoneNumber;
    this.csatRating = csatRating;
    this.finLegRecordingUrl = finLegRecordingUrl;
    this.finLegJsonTranscript = finLegJsonTranscript;
    this.finLegTranscriptUrl = finLegTranscriptUrl;
    this.terminatingPartyType = terminatingPartyType;
  }

  // Backwards-compatible getters for accessing recordings
  get mainRecordings(): CallRecording[] {
    return this.recordings.filter((r) => r.recordingType === 'call');
  }

  get finRecordings(): CallRecording[] {
    return this.recordings.filter((r) => r.recordingType === 'fin_voice');
  }

  get hasRecordings(): boolean {
    return this.recordings.length > 0;
  }

  get hasMainRecordings(): boolean {
    return this.mainRecordings.length > 0;
  }

  get hasFinRecordings(): boolean {
    return this.finRecordings.length > 0;
  }

  static deserialize(json: CallingDetailWireFormat): CallingDetail {
    // Deserialize recordings array if present, otherwise create from legacy fields
    let recordings =
      Array.isArray(json.recordings) && json.recordings.length > 0
        ? json.recordings
            .map((r) => deserializeCallRecording(r))
            .filter((r): r is CallRecording => r !== null)
        : createLegacyRecordingsFromFields(json);

    return new CallingDetail(
      json.id,
      json.external_id,
      json.recording_enabled,
      json.type,
      json.state,
      json.direction,
      json.ended_reason,
      json.duration,
      json.answered_at,
      json.created_at,
      json.initiated_at,
      json.ended_at,
      json.routing_time,
      json.user_id,
      json.admin_id,
      json.phone,
      json.human_transcript,
      json.json_transcript,
      json.recording_url,
      json.conversation_id,
      json.transcript_url,
      json.transcript_status,
      json.recording_name,
      json.forwarded_to,
      json.detailed_state,
      json.is_recorded,
      json.workspace_phone_number,
      json.csat_rating,
      json.fin_leg_recording_url,
      json.fin_leg_json_transcript,
      json.fin_leg_transcript_url,
      json.terminating_party_type,
      recordings,
    );
  }

  static serialize(callDetail: CallingDetail): CallingDetailWireFormat {
    return {
      id: callDetail.id,
      external_id: callDetail.externalId,
      recording_enabled: callDetail.recordingEnabled,
      type: callDetail.type,
      state: callDetail.state,
      direction: callDetail.direction,
      ended_reason: callDetail.endedReason,
      duration: callDetail.duration,
      answered_at: callDetail.answeredAt,
      created_at: callDetail.createdAt,
      initiated_at: callDetail.initiatedAt,
      ended_at: callDetail.endedAt,
      routing_time: callDetail.routingTime,
      user_id: callDetail.userId,
      admin_id: callDetail.adminId,
      phone: callDetail.phone,
      human_transcript: callDetail.humanTranscript,
      json_transcript: callDetail.jsonTranscript,
      recording_url: callDetail.recordingUrl,
      conversation_id: callDetail.conversationId,
      transcript_url: callDetail.transcriptUrl,
      transcript_status: callDetail.transcriptStatus,
      recording_name: callDetail.recordingName,
      forwarded_to: callDetail.forwardedTo,
      detailed_state: callDetail.detailedState,
      is_recorded: callDetail.isRecorded,
      workspace_phone_number: callDetail.workspacePhoneNumber,
      csat_rating: callDetail.csatRating,
      fin_leg_recording_url: callDetail.finLegRecordingUrl,
      fin_leg_json_transcript: callDetail.finLegJsonTranscript,
      fin_leg_transcript_url: callDetail.finLegTranscriptUrl,
      terminating_party_type: callDetail.terminatingPartyType,
      recordings: callDetail.recordings.map(serializeCallRecording),
    };
  }
}
